import { calculateCoachingInfluence, seededNormal } from './matchProbability'
import { OPPONENT_COACHES } from './seasonData'
import { stableHash } from './deterministic'
import type { CommittedMatchContext, ScheduledFixture, SeasonState, TeamId } from './types'

export const createCareerSeed = (input?: string) => {
  const values = new Uint32Array(4)
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(values)
  else {
    const base = input ? stableHash(input) : 123456789
    values.set([base, Math.imul(base, 31), Math.imul(base, 131), Math.imul(base, 521)])
  }
  return Array.from(values, value => value.toString(36)).join('-')
}

export const hydrateSeasonSimulation = (season: SeasonState, fallbackSeed?: string): SeasonState => ({
  ...season,
  careerSeed: season.careerSeed ?? fallbackSeed ?? `legacy-${season.seed}`,
  matchContexts: season.matchContexts ?? {},
})

const opponentIdForFixture = (season: SeasonState, fixture: ScheduledFixture): TeamId =>
  fixture.homeTeamId === season.userTeamId ? fixture.awayTeamId : fixture.homeTeamId

const planApproach = (
  preferred: 'conservative' | 'balanced' | 'aggressive' | 'analytical',
  ratingDifference: number,
  riskAppetite: number,
) => {
  if (ratingDifference <= -6 && riskAppetite >= 60) return 'aggressive' as const
  if (ratingDifference >= 8 && riskAppetite <= 55) return 'conservative' as const
  return preferred
}

const interventionCountFor = (approach: 'conservative' | 'balanced' | 'aggressive' | 'analytical', seed: string) => {
  const baseline = approach === 'conservative' ? 1 : approach === 'balanced' ? 2 : approach === 'analytical' ? 3 : 5
  return Math.max(1, Math.min(6, baseline + Math.round(seededNormal(seed, 'opponent-plan-complexity') * 0.65)))
}

export const buildCommittedMatchContext = (rawSeason: SeasonState, fixture: ScheduledFixture): CommittedMatchContext => {
  const season = hydrateSeasonSimulation(rawSeason)
  const opponentId = opponentIdForFixture(season, fixture)
  const profile = OPPONENT_COACHES[opponentId]
  const opponentRating = season.teams[opponentId].simulationRating
  const kingsportRating = season.teams[season.userTeamId].simulationRating
  const ratingDifference = opponentRating - kingsportRating
  const seed = `${season.careerSeed}:season-${season.season}:${fixture.id}`
  const approach = planApproach(profile.preferredApproach, ratingDifference, profile.riskAppetite)
  const interventionCount = interventionCountFor(approach, seed)
  const alignedResearchCount = Math.max(0, Math.min(interventionCount, Math.floor(profile.preparation / 30)))
  const rawMarginEdge = ((profile.tacticalAbility - 62) / 4) + (alignedResearchCount * 1.25) + (approach === 'aggressive' ? 0.75 : 0)
  const influence = calculateCoachingInfluence({
    rawMarginEdge,
    interventionCount,
    alignedResearchCount,
    tacticalCohesion: profile.adaptability,
    seed: `${seed}:opponent-coach`,
  })
  const executionDescription = influence.executionRisk >= 4
    ? 'accepted substantial execution risk'
    : influence.executionRisk > 0
      ? 'accepted controlled execution risk'
      : 'kept execution risk low'

  return {
    fixtureId: fixture.id,
    seed,
    kingsportPerformance: seededNormal(seed, 'kingsport-performance'),
    opponentPerformance: seededNormal(seed, 'opponent-performance'),
    opponentPlan: {
      coachName: profile.name,
      approach,
      interventionLevel: influence.interventionLevel,
      expectedMarginEdge: influence.expectedMarginEdge,
      executionRisk: influence.executionRisk,
      realisedMarginEdge: influence.realisedMarginEdge,
      explanation: `${profile.name} used a ${approach} plan and ${executionDescription}.`,
    },
  }
}

export const commitMatchContext = (rawSeason: SeasonState, fixture: ScheduledFixture) => {
  const season = hydrateSeasonSimulation(rawSeason)
  const existing = season.matchContexts?.[fixture.id]
  if (existing) return { season, context: existing }
  const context = buildCommittedMatchContext(season, fixture)
  return {
    season: { ...season, matchContexts: { ...season.matchContexts, [fixture.id]: context } },
    context,
  }
}

export const commitCurrentMatchContext = <T extends { round: number; season?: SeasonState }>(state: T): T => {
  if (!state.season) return state
  const fixture = state.round > 19
    ? state.season.finals?.fixtures.find(candidate => candidate.id === (state as T & { week?: { fixtureId: string } }).week?.fixtureId)
    : state.season.fixtures.find(candidate =>
      candidate.round === state.round
      && (candidate.homeTeamId === state.season!.userTeamId || candidate.awayTeamId === state.season!.userTeamId))
  if (!fixture) return state
  const scheduled: ScheduledFixture = 'slot' in fixture
    ? { id: fixture.id, round: 19 + fixture.week, homeTeamId: fixture.homeTeamId, awayTeamId: fixture.awayTeamId, seed: stableHash(fixture.id), venue: fixture.venue, stage: fixture.stage }
    : fixture
  const committed = commitMatchContext(state.season, scheduled)
  return { ...state, season: committed.season }
}

export const contextPerformanceMargin = (context: CommittedMatchContext, teamStandardDeviation = Math.SQRT1_2 * 20) =>
  (context.kingsportPerformance - context.opponentPerformance) * teamStandardDeviation
